import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiX, FiZap, FiAlertCircle, FiShield } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Capabilities = () => {
  const capabilities = [
    'Answers every call in under 2 seconds, 24/7/365',
    'Greets callers professionally with your company name',
    'Asks diagnostic questions to identify the issue',
    'Handles Tier-1 calls: password resets, order status, basic troubleshooting',
    'Creates support tickets automatically in your helpdesk',
    'Routes complex issues to the right department or agent',
    'Transfers to your team with full call context and transcript',
    'Speaks English + Spanish (more languages available)',
    'Logs every call with recording, transcript, and issue summary',
  ];

  const limitations = [
    'Provide legal, compliance, or regulatory advice',
    'Handle issues requiring screen sharing or remote access',
    'Make promises about refunds, SLAs, or exceptions',
    'Handle deeply emotional or crisis situations (escalates immediately)',
    'Perform complex multi-system troubleshooting without human judgment',
    'Continue if caller explicitly requests a human agent',
  ];

  return (
    <section className="py-12 md:py-16 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
      <div className="absolute -left-64 top-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-10 md:mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-widest mb-4 shadow-sm">
            <SafeIcon icon={FiShield} className="w-3 h-3 text-green-600" />
            Operational Transparency
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What the AI Handles vs. Escalates</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Our AI handles 65–70% of routine interactions. Our escalation protocol ensures complex situations are handed off to your team immediately — with full context.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-slate-500"></div>
            <div className="flex items-center gap-5 mb-8">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-green-50 flex items-center justify-center border border-green-100">
                <SafeIcon icon={FiZap} className="w-6 h-6 md:w-7 md:h-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">AI Handles</h3>
                <p className="text-slate-500 text-sm font-medium">Routine Tier-1 support interactions</p>
              </div>
            </div>
            <div className="space-y-4">
              {capabilities.map((item, i) => (
                <div key={i} className="flex items-start gap-4 group/item">
                  <div className="w-6 h-6 rounded-full bg-green-50 border border-green-100 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-green-600 group-hover/item:border-green-600 transition-all duration-300">
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-green-600 group-hover/item:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-slate-700 font-medium text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-300 to-slate-200"></div>
            <div className="flex items-center gap-5 mb-8">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-200">
                <SafeIcon icon={FiAlertCircle} className="w-6 h-6 md:w-7 md:h-7 text-slate-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">What AI Escalates</h3>
                <p className="text-slate-500 text-sm font-medium">Complex & sensitive situations</p>
              </div>
            </div>
            <div className="space-y-4">
              {limitations.map((item, i) => (
                <div key={i} className="flex items-start gap-4 opacity-80">
                  <div className="w-6 h-6 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <SafeIcon icon={FiX} className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <span className="text-slate-600 font-medium text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <p className="text-xs text-slate-900 font-bold uppercase tracking-wider">Smart Escalation Protocol</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                <p className="text-sm text-slate-600 leading-relaxed">
                  When any limitation is triggered, the AI responds: <br />
                  <span className="italic text-slate-800 font-medium">"Let me connect you with a specialist who can help with that,"</span> and transfers the call with the full transcript and issue summary already waiting for your agent.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Capabilities;